import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatInstant } from '@/shared/format/instant';
import {
  SWIPE_SLOP_PX,
  centreOf,
  containRect,
  flyTransform,
  rectInView,
  swipeAxis,
  swipeVerdict,
  zoomTransform,
  type Rect,
  type Size,
  type SwipeAxis,
  type SwipeLimits,
} from '@/shared/image/viewerMath';
import { strings } from '@/shared/strings';
import { CameraIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, DownloadIcon } from '@/ui/icons';
import { useMediaQuery } from '@/ui/layout/useMediaQuery';
import { DURATION, EASE, EXIT, SHIFT, reducedMotion } from '@/ui/motion/motion';
import { FLICK_MIN_PX, FLICK_VELOCITY, SWIPE_CLOSE_PX } from './BottomSheet';
import styles from './ImageViewer.module.css';
import { useOverlay } from './useOverlay';
import { usePinchZoom, type PinchDrag, type PinchDragEnd } from './usePinchZoom';

/** One photograph in the viewer. */
export interface ViewerImage {
  id: string;
  /** The full file, or null while its bytes are still on their way. */
  src: string | null;
  /** A small version to show first, or null when there is none. The same as `src` when there is one file. */
  thumbSrc: string | null;
  /** Who uploaded it and when; null for a photograph still on the device. */
  uploadedByName: string | null;
  uploadedAt: string | null;
  /** What it shows: the stage, "Not sent yet". Its name for a screen reader, and printed under it. */
  caption?: string;
  /**
   * The full file is not coming: its bytes could not be fetched. Said in place
   * of "Loading the photograph", which would otherwise be said for ever.
   */
  failed?: boolean;
}

export interface ImageViewerProps {
  images: readonly ViewerImage[];
  /** Which one is on show. The owner's: the viewer asks for the next one through `onIndexChange`. */
  index: number;
  onIndexChange: (index: number) => void;
  /** Where the tile that was tapped is, for the photograph to grow out of; null to come up in place. */
  origin: DOMRect | null;
  /**
   * Where the tile of a photograph is now, for the one on show to shrink back
   * into on closing: after a swipe it is a different tile, and it may have
   * been scrolled away. Without it only the photograph the viewer opened on
   * goes back to its tile.
   */
  originOf?: (index: number) => DOMRect | null;
  onClose: () => void;
  /** Save the photograph on show. No button at all when undefined: the owner knows who may. */
  onDownload?: (image: ViewerImage) => void;
  /** The download is on its way: the button waits. */
  downloading?: boolean;
  /** The strip of every photograph at the bottom. When there is more than one, unless told otherwise. */
  thumbnails?: boolean;
}

/** The sheet's own numbers, so a pull that closes a sheet closes a photograph. */
const SWIPE: SwipeLimits = {
  slop: SWIPE_SLOP_PX,
  distance: SWIPE_CLOSE_PX,
  flickVelocity: FLICK_VELOCITY,
  flickMin: FLICK_MIN_PX,
};

/** Between one photograph and the next on the track, so they never touch mid-swipe. */
const SLIDE_GAP = SHIFT.md;

/** Dragged past the first or the last: the track follows the finger at a third of the way. */
const END_RESISTANCE = 3;

/** How the black lifts as the photograph is pulled down: gone by this far, never below this much. */
const SCRIM_FADE_PX = 480;
const SCRIM_FLOOR = 0.25;

/** A mouse, and the hover it has: the only place the buttons at the sides are any use. */
const FINE_POINTER = '(hover: hover) and (pointer: fine)';

/** The keys the viewer takes, so that none of them reaches whatever is under it. */
const KEYS = new Set(['Escape', 'ArrowLeft', 'ArrowRight']);

const canAnimate = () => typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';

/**
 * The one image viewer, over whatever screen a photograph was on. The request
 * of 5 October 2026 (third), section 6.
 *
 * Presentational: the owner resolves the bytes and the permissions and hands
 * over `images`, `index` and the callbacks. A dialog over everything (its own
 * z-index token), on black, keeping the notch and the home indicator clear.
 * The counter, the download button when the owner allows one, and the cross
 * in the head; the photograph on a stage that takes every gesture; who
 * uploaded it and when, and the strip of all of them, in the foot.
 *
 * Swipe across for the next or the one before (buttons at the sides for a
 * mouse, the arrow keys for anybody); pinch, double tap or the wheel to zoom,
 * and drag to move about once zoomed; the cross, a pull down or Escape to
 * close. The keys are caught on their way down and go no further, which is
 * what keeps the sheet, page, dialog or drawer underneath open.
 *
 * The photograph grows out of its tile on opening and shrinks back into it on
 * closing, where there is one on the screen to go to; a fade otherwise, and
 * under reduced motion. The thumbnail is drawn first and the full file fades
 * in over it once decoded; only the photograph on show has its full file
 * mounted, the ones beside it draw their thumbnails alone.
 */
export function ImageViewer({
  images,
  index,
  onIndexChange,
  origin,
  originOf,
  onClose,
  onDownload,
  downloading = false,
  thumbnails,
}: ImageViewerProps) {
  const total = images.length;
  const at = Math.min(Math.max(index, 0), Math.max(total - 1, 0));
  const current = images[at];

  const stage = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const fly = useRef<HTMLDivElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  /** The size of each photograph once something of it has loaded, by id. */
  const [shapes, setShapes] = useState<Record<string, Size>>({});
  /** A finger's drag of the track: across between photographs, or down to close. */
  const [drag, setDrag] = useState<{ axis: SwipeAxis; by: number } | null>(null);
  const dragAxis = useRef<SwipeAxis | null>(null);
  /** Let go short of a verdict: the track eases back. */
  const [springing, setSpringing] = useState(false);
  /** On its way out: flying back to a tile, or fading where it is. */
  const [leaving, setLeaving] = useState<'no' | 'fly' | 'fade'>('no');
  const gone = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The photograph the viewer opened on: `origin` is its tile. */
  const [openedAt] = useState(at);
  /** Whether it grows out of its tile on opening; decided once, before the first paint. */
  const [grows] = useState(() => origin !== null && !reducedMotion() && canAnimate());

  /** Which photograph is drawn, as of the last commit; the keys and the slide read it. */
  const shown = useRef(at);
  /** Whether the photograph on show is zoomed in, for the way out to read. */
  const pinchZoomed = useRef(false);

  const fine = useMediaQuery(FINE_POINTER);
  const shape = current === undefined ? null : (shapes[current.id] ?? null);

  const close = useRef(onClose);
  close.current = onClose;

  useOverlay();

  /** The transform that puts the photograph on show onto a tile. */
  const towards = useCallback(
    (tile: Rect): string | null => {
      const frame = fly.current;
      if (frame === null) return null;
      const around = frame.getBoundingClientRect();
      // Until something of it has loaded, its shape is not known and is taken to be the tile's.
      const shown = containRect(shape ?? tile, around);
      return flyTransform(tile, shown, centreOf(around));
    },
    [shape],
  );

  /** Closing, every way: once. */
  const leave = useCallback(() => {
    if (gone.current) return;
    gone.current = true;

    const frame = fly.current;
    const tile = pinchZoomed.current
      ? null
      : originOf !== undefined
        ? originOf(at)
        : at === openedAt
          ? origin
          : null;
    let end: string | null = null;
    if (
      tile !== null &&
      frame !== null &&
      typeof frame.animate === 'function' &&
      !reducedMotion() &&
      rectInView(tile, { width: window.innerWidth, height: window.innerHeight })
    ) {
      // Whatever it was still doing, so the measure is of where it stands.
      frame.getAnimations?.().forEach((animation) => animation.cancel());
      end = towards(tile);
    }
    if (end !== null && frame !== null) {
      frame.animate([{ transform: 'none' }, { transform: end }], {
        duration: EXIT.normal,
        easing: EASE.exit,
        fill: 'forwards',
      });
    }
    setLeaving(end === null ? 'fade' : 'fly');
    timer.current = setTimeout(() => close.current(), EXIT.normal);
  }, [at, openedAt, origin, originOf, towards]);

  const go = useCallback(
    (target: number) => {
      if (gone.current || target === at || target < 0 || target >= total) return;
      onIndexChange(target);
    },
    [at, onIndexChange, total],
  );

  /** The drag's offset the moment it turned a page, so the slide carries on from the finger. */
  const carried = useRef(0);

  function onDragMove({ dx, dy }: PinchDrag) {
    let axis = dragAxis.current;
    if (axis === null) {
      axis = swipeAxis(dx, dy, SWIPE_SLOP_PX);
      if (axis === null) return;
      dragAxis.current = axis;
      setSpringing(false);
    }
    if (axis === 'x') {
      const beyond = (dx > 0 && at === 0) || (dx < 0 && at === total - 1);
      setDrag({ axis, by: beyond ? dx / END_RESISTANCE : dx });
    } else {
      setDrag({ axis, by: Math.max(0, dy) });
    }
  }

  function onDragEnd({ dx, dy, velocity, moves }: PinchDragEnd) {
    const axis = dragAxis.current;
    dragAxis.current = null;
    if (axis === null) return;
    const verdict =
      axis === 'x' ? swipeVerdict(dx, 0, velocity, moves, SWIPE) : swipeVerdict(0, dy, velocity, moves, SWIPE);
    if (verdict === 'close') {
      // It stays where the finger left it, and goes from there.
      leave();
      return;
    }
    const step = verdict === 'next' ? 1 : verdict === 'previous' ? -1 : 0;
    const target = at + step;
    if (step !== 0 && target >= 0 && target < total) {
      carried.current = dx;
      setDrag(null);
      go(target);
      return;
    }
    setSpringing(true);
    setDrag(null);
  }

  const pinch = usePinchZoom(stage, {
    natural: shape,
    disabled: leaving !== 'no',
    onDragMove,
    onDragEnd,
  });
  pinchZoomed.current = pinch.zoomed;
  const { reset } = pinch;

  const leaveNow = useRef(leave);
  leaveNow.current = leave;
  const goNow = useRef(go);
  goNow.current = go;

  /*
   * The keys, caught on their way down to the document and stopped there. The
   * sheet, the full-screen page, the dialog and the drawer each close on an
   * Escape that reaches the document; one from the viewer must close the
   * viewer alone.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!KEYS.has(event.key)) return;
      event.stopPropagation();
      if (event.key === 'Escape') {
        leaveNow.current();
        return;
      }
      event.preventDefault();
      const step = event.key === 'ArrowLeft' ? -1 : 1;
      goNow.current(shown.current + step);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // Focus starts on the cross; useOverlay gives it back to the tile on closing.
  useEffect(() => {
    closeButton.current?.focus({ preventScroll: true });
  }, []);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  /*
   * Opening: the photograph grows out of its tile. Played before the first
   * paint, from where the tile is to where the photograph will be shown.
   */
  useLayoutEffect(() => {
    if (!grows || origin === null) return;
    const start = towards(origin);
    if (start === null) return;
    fly.current?.animate([{ transform: start }, { transform: 'none' }], {
      duration: DURATION.normal,
      easing: EASE.enter,
    });
    // Once, on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * A new photograph on show: the zoom starts again, and the track slides
   * from the side it came from (carrying on from the finger after a swipe),
   * or fades in when it came from further away or under reduced motion.
   * Drawn in its final place first; the slide is over the top of that.
   */
  useLayoutEffect(() => {
    const before = shown.current;
    shown.current = at;
    if (before === at) return;
    reset();
    const step = at - before;
    const offset = carried.current;
    carried.current = 0;
    const node = track.current;
    const room = stage.current;
    if (node === null || room === null || typeof node.animate !== 'function') return;
    if (Math.abs(step) === 1 && !reducedMotion()) {
      const from = step * (room.getBoundingClientRect().width + SLIDE_GAP) + offset;
      node.animate([{ transform: `translateX(${String(from)}px)` }, { transform: 'none' }], {
        duration: DURATION.normal,
        easing: EASE.spring,
      });
    } else {
      node.animate([{ opacity: 0 }, { opacity: 1 }], { duration: DURATION.fast, easing: EASE.enter });
    }
  }, [at, reset]);

  // The strip alone is moved, by its own scroll position, to keep the one on show in it.
  useEffect(() => {
    const box = strip.current;
    const tile = box?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!box || !tile) return;
    const edge = box.getBoundingClientRect();
    const own = tile.getBoundingClientRect();
    if (own.left < edge.left) box.scrollLeft -= edge.left - own.left;
    else if (own.right > edge.right) box.scrollLeft += own.right - edge.right;
  }, [at]);

  const learnShape = useCallback((id: string, size: Size) => {
    setShapes((known) => {
      const had = known[id];
      if (had !== undefined && had.width === size.width && had.height === size.height) return known;
      return { ...known, [id]: size };
    });
  }, []);

  if (current === undefined) return null;

  const label = current.caption ?? strings.viewer.position(at + 1, total);
  const withStrip = thumbnails ?? total > 1;
  const beside = [-1, 0, 1]
    .map((step) => ({ step, image: images[at + step] }))
    .filter((slide): slide is { step: number; image: ViewerImage } => slide.image !== undefined);

  const trackStyle =
    drag === null
      ? undefined
      : { transform: drag.axis === 'x' ? `translateX(${String(drag.by)}px)` : `translateY(${String(drag.by)}px)` };
  const scrimStyle =
    drag !== null && drag.axis === 'y'
      ? { opacity: Math.max(SCRIM_FLOOR, 1 - drag.by / SCRIM_FADE_PX) }
      : undefined;

  return (
    <div
      className={leaving === 'no' ? styles.viewer : `${styles.viewer} ${styles.leaving}`}
      role="dialog"
      aria-modal="true"
      aria-label={strings.viewer.title}
    >
      <div className={styles.scrim} style={scrimStyle} />

      <header className={styles.head}>
        <p className={styles.counter} aria-live="polite">
          {strings.viewer.counter(at + 1, total)}
        </p>
        {onDownload !== undefined && (
          <button
            type="button"
            className={styles.control}
            aria-label={downloading ? strings.viewer.downloading : strings.viewer.download}
            aria-busy={downloading || undefined}
            disabled={downloading}
            onClick={() => onDownload(current)}
          >
            <DownloadIcon size={20} />
          </button>
        )}
        <button
          ref={closeButton}
          type="button"
          className={styles.control}
          aria-label={strings.viewer.close}
          onClick={leave}
        >
          <CloseIcon size={20} />
        </button>
      </header>

      <div className={styles.middle}>
        <div
          ref={stage}
          className={styles.stage}
          data-testid="viewer-stage"
          onContextMenu={(event) => event.preventDefault()}
          {...pinch.handlers}
        >
          <div
            ref={track}
            className={[styles.track, grows ? '' : styles.pop, springing ? styles.springing : '']
              .filter(Boolean)
              .join(' ')}
            style={trackStyle}
          >
            {beside.map(({ step, image }) => {
              const onShow = step === 0;
              return (
                <div
                  key={image.id}
                  className={styles.slide}
                  style={
                    onShow
                      ? undefined
                      : { transform: `translateX(calc(${String(step * 100)}% + ${String(step * SLIDE_GAP)}px))` }
                  }
                  role={onShow ? 'img' : undefined}
                  aria-label={onShow ? label : undefined}
                  aria-hidden={onShow ? undefined : true}
                  data-slide={onShow ? 'current' : 'beside'}
                >
                  <div
                    ref={onShow ? fly : undefined}
                    className={onShow && leaving === 'fade' ? `${styles.fly} ${styles.fading}` : styles.fly}
                  >
                    <div
                      className={onShow && pinch.following ? `${styles.zoom} ${styles.following}` : styles.zoom}
                      style={onShow ? { transform: zoomTransform(pinch.view) } : undefined}
                      data-testid={onShow ? 'viewer-zoom' : undefined}
                    >
                      <Picture image={image} onShow={onShow} onShape={learnShape} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {fine && total > 1 && (
          <>
            <button
              type="button"
              className={`${styles.control} ${styles.nav} ${styles.previous}`}
              aria-label={strings.viewer.previous}
              disabled={at === 0}
              onClick={() => go(at - 1)}
            >
              <ChevronLeftIcon size={22} />
            </button>
            <button
              type="button"
              className={`${styles.control} ${styles.nav} ${styles.next}`}
              aria-label={strings.viewer.next}
              disabled={at === total - 1}
              onClick={() => go(at + 1)}
            >
              <ChevronRightIcon size={22} />
            </button>
          </>
        )}
      </div>

      <footer className={styles.foot}>
        <p className={styles.who}>
          {current.caption !== undefined && <span>{current.caption}</span>}
          {current.uploadedByName !== null && <b>{strings.viewer.takenBy(current.uploadedByName)}</b>}
          {current.uploadedAt !== null && (
            <time dateTime={current.uploadedAt}>{formatInstant(current.uploadedAt)}</time>
          )}
        </p>
        {withStrip && (
          <div ref={strip} className={styles.strip} role="group" aria-label={strings.viewer.thumbnails}>
            {images.map((image, i) => (
              <button
                key={image.id}
                type="button"
                className={styles.tile}
                aria-label={strings.viewer.position(i + 1, total)}
                aria-current={i === at ? 'true' : undefined}
                onClick={() => go(i)}
              >
                {image.thumbSrc !== null ? (
                  <img src={image.thumbSrc} alt="" draggable={false} loading="lazy" decoding="async" />
                ) : (
                  <CameraIcon size={18} />
                )}
              </button>
            ))}
          </div>
        )}
      </footer>
    </div>
  );
}

/**
 * One photograph on the track: the thumbnail, and over it, for the one on
 * show alone, the full file once it has been decoded. One file only when the
 * thumbnail is the full file, as every photograph stored before thumbnails
 * were made is; a camera and a word while there is nothing to draw yet, or
 * nothing to draw at all.
 */
function Picture({
  image,
  onShow,
  onShape,
}: {
  image: ViewerImage;
  onShow: boolean;
  onShape: (id: string, size: Size) => void;
}) {
  /** The file that has been decoded and may be shown. */
  const [decoded, setDecoded] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  // Off show, the full file goes; back on show it is decoded again before it is seen.
  useEffect(() => {
    if (!onShow) setDecoded(null);
  }, [onShow]);

  const under = image.thumbSrc;
  const full = onShow && image.src !== null && image.src !== image.thumbSrc ? image.src : null;
  const ready = full !== null && decoded === full;
  // Not drawable, or never fetched: either way there is no full file to wait for.
  const broken = (full !== null && failed === full) || (image.failed === true && image.src === null);
  const empty = under === null && !ready;

  const learn = (node: HTMLImageElement) => {
    if (node.naturalWidth > 0 && node.naturalHeight > 0) {
      onShape(image.id, { width: node.naturalWidth, height: node.naturalHeight });
    }
  };

  return (
    <>
      {under !== null && (
        <img
          className={styles.picture}
          data-layer="thumb"
          src={under}
          alt=""
          draggable={false}
          decoding="async"
          onLoad={(event) => learn(event.currentTarget)}
        />
      )}
      {full !== null && (
        <img
          className={`${styles.picture} ${styles.full}`}
          data-layer="full"
          data-ready={ready ? 'true' : 'false'}
          src={full}
          alt=""
          draggable={false}
          decoding="async"
          onLoad={(event) => {
            const node = event.currentTarget;
            learn(node);
            // Shown once decoded, so the fade is onto a picture and not onto a blank being filled in.
            const show = () => setDecoded(full);
            if (typeof node.decode === 'function') void node.decode().then(show, show);
            else show();
          }}
          onError={() => setFailed(full)}
        />
      )}
      {onShow && (empty || broken) && (
        <div className={styles.mark} role="status">
          <CameraIcon size={28} />
          <span>{broken ? strings.viewer.failed : strings.viewer.loading}</span>
        </div>
      )}
    </>
  );
}
